/** SKUs bought but never deployed — nobody has ever been assigned one.
 *
 *  The cleanest reclaim there is: cancelling these costs nobody their access,
 *  because nobody has any.
 *
 *  "Bought" means purchased seats only. A subscription that has expired with
 *  nobody on it is not an undeployed purchase — it is a subscription that has
 *  already ended, and it belongs in the expiring-capacity panel instead. */

import { Box, Icon } from "@norican/icons";
import { Card, DataGrid, EmptyState, Typography } from "@norican/react";

import type { LicenseSkuRow } from "@/lib/api";

import { formatMoney, formatSeats } from "./licence-format";

interface ZeroAssignmentProps {
  rows: LicenseSkuRow[];
  currency: string;
  pricesConfigured: boolean;
}

export function ZeroAssignment({ rows, currency, pricesConfigured }: ZeroAssignmentProps) {
  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Bought, never deployed
          </Typography>
          <Typography variant="body-sm" tone="muted">
            Zero assignments since purchase
          </Typography>
        </div>
      </Card.Header>
      <Card.Body padding={rows.length === 0 ? "md" : "none"}>
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<Icon icon={Box} size="lg" />}
            title="Every paid SKU is in use"
            description="Nothing has been purchased and left entirely unassigned."
          />
        ) : (
          <DataGrid<LicenseSkuRow>
            data={rows}
            getRowId={(sku) => sku.sku_id || sku.sku_part_number}
            density="compact"
            aria-label="SKUs with no assignments"
            columns={[
              {
                id: "name",
                header: "SKU",
                accessor: (sku) => sku.name,
                sortable: true,
                cell: (sku) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{sku.name}</span>
                    <span className="text-fg-muted text-xs">{sku.sku_part_number}</span>
                  </div>
                ),
              },
              {
                id: "seats",
                header: "Paid seats",
                numeric: true,
                sortable: true,
                accessor: (sku) => sku.purchased,
                cell: (sku) => (
                  <span className="text-warning-fg font-medium tabular-nums">
                    {formatSeats(sku.purchased)}
                  </span>
                ),
              },
              ...(pricesConfigured
                ? [
                    {
                      id: "cost",
                      header: "Est. monthly cost",
                      numeric: true,
                      sortable: true,
                      accessor: (sku: LicenseSkuRow) => sku.estimated_monthly_waste ?? 0,
                      cell: (sku: LicenseSkuRow) => {
                        const money = formatMoney(sku.estimated_monthly_waste, currency);
                        return money ? (
                          <span className="text-danger-fg tabular-nums">{money}</span>
                        ) : (
                          <span className="text-fg-subtle text-xs">No rate set</span>
                        );
                      },
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Card.Body>
    </Card>
  );
}
